import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import BaseModal from "./BaseModal";
import { paymentApi } from "../../services/api";
import { readCards } from "../../services/paymentStorage";
import { toast } from "sonner";


import { useAuth } from "../../context/AuthContext";

export default function PaymentModal({ onClose, onRequestAddCard, onSuccess, onError }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [cards, setCards] = useState(() => readCards());
  const [selectedCardId, setSelectedCardId] = useState(cards[0]?.id || "");
  const [amountText, setAmountText] = useState("125 400");
  const [isPaying, setIsPaying] = useState(false);
  function onlyDigits(value) {
    return String(value || "").replace(/\D/g, "");
  }
  
  function formatAmount(value) {
    const digits = onlyDigits(value).slice(0, 9);
    if (!digits) return "";
    return Number(digits).toLocaleString("en-US").replace(/,/g, " ");
  }

  useEffect(() => {
    const refreshed = readCards();
    setCards(refreshed);
    setSelectedCardId((prev) => prev || refreshed[0]?.id || "");
  }, []);

  const amount = useMemo(() => Number(onlyDigits(amountText) || 0), [amountText]);

  const submit = async () => {
    if (!selectedCardId) {
      toast.error(t("payment.errors.noCard"));
      return;
    }
    if (!amount || amount < 1000) {
      toast.error(t("payment.errors.minAmount"));
      return;
    }
    setIsPaying(true);
    try {
      const result = await paymentApi.topup({ 
        userId: user?.id,
        amount, 
        providerRef: `card_${selectedCardId}_${Date.now()}`
      });
      toast.success(t("payment.messages.success"));
      onSuccess?.(result);
      onClose?.();
    } catch (e) {
      toast.error(e.message || t("payment.errors.failed"));
      onError?.(e);
    } finally {
      setIsPaying(false);
    }
  };

  return (
    <BaseModal
      title={t("billing.pay.title")}
      onClose={isPaying ? undefined : onClose}
      maxWidthClass="max-w-md"
    >
      <div className="space-y-8 py-2">
        {/* Joriy balans / Qarzdorlik */}
        <div className="bg-gradient-to-br from-[#1e9f6b] to-[#0d7a52] rounded-[32px] p-7 text-white relative overflow-hidden shadow-2xl shadow-[#1e9f6b]/20">
          <div className="absolute -top-4 -right-4 h-32 w-32 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-8 -left-8 h-32 w-32 bg-black/10 rounded-full blur-3xl" />
          
          <div className="relative z-10 space-y-1">
            <p className="text-white/70 text-[10px] font-black uppercase tracking-[0.2em]">
              {t("billing.pay.currentDebt")}
            </p>
            <h3 className="text-4xl font-black italic tracking-tighter">
              125 400 <span className="text-sm font-bold not-italic opacity-60 uppercase ml-1">UZS</span>
            </h3>
          </div>
        </div>

        {/* Karta tanlash */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
              {t("billing.pay.method")}
            </label>
            {cards.length > 0 && (
              <span className="text-[10px] font-black text-[#1e9f6b] uppercase tracking-widest bg-[#1e9f6b]/10 px-2 py-1 rounded-lg">
                {cards.length} {t("billing.pay.selected")}
              </span>
            )}
          </div>

          <div className="space-y-3 max-h-[220px] overflow-y-auto pr-2 custom-scrollbar">
            {cards.map((c) => {
              const active = c.id === selectedCardId;
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedCardId(c.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all duration-300 ${
                    active
                      ? 'border-[#1e9f6b] bg-[#1e9f6b]/5 dark:bg-[#1e9f6b]/10 shadow-lg shadow-[#1e9f6b]/5'
                      : 'border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:border-slate-200 dark:hover:border-slate-700'
                  }`}
                >
                  <div className={`w-12 h-8 rounded-lg flex items-center justify-center shadow-lg transition-transform duration-300 ${
                    active ? 'scale-105' : ''
                  } ${
                    c.brand === 'uzcard' ? 'bg-[#005696]' : 'bg-[#f7931e]'
                  }`}>
                    <span className="text-[9px] font-black text-white italic tracking-tighter">{c.brand.toUpperCase()}</span>
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`text-sm font-black italic tracking-tight ${active ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-400'}`}>
                      •••• {c.last4}
                    </p>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{c.holder}</p>
                  </div>
                  <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                    active ? 'border-[#1e9f6b] bg-[#1e9f6b]' : 'border-slate-200 dark:border-slate-700'
                  }`}>
                    {active && <span className="material-symbols-outlined text-white text-[16px]">check</span>}
                  </div>
                </button>
              );
            })}
            
            <button
              onClick={() => onRequestAddCard?.()}
              className="w-auto flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-800 text-slate-400 hover:border-[#1e9f6b] hover:text-[#1e9f6b] hover:bg-[#1e9f6b]/5 transition-all group"
            >
              <span className="material-symbols-outlined text-[18px] group-hover:rotate-90 transition-transform">add</span>
              <span className="text-[10px] font-black uppercase tracking-widest">{t("billing.pay.addCard")}</span>
            </button>
          </div>
        </div>

        {/* Summa */}
        <div className="space-y-4">
          <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em] px-1">
            {t("billing.pay.amount")}
          </label>
          <div className="relative group">
            <input
              type="text"
              value={amountText}
              onChange={(e) => setAmountText(formatAmount(e.target.value))}
              className="w-full h-[72px] bg-slate-50 dark:bg-slate-800/50 border-2 border-transparent focus:border-[#1e9f6b] rounded-[24px] px-7 text-3xl font-black text-slate-900 dark:text-white outline-none transition-all italic tracking-tighter"
              placeholder="0"
            />
            <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-3">
              <span className="text-xs font-black text-slate-300 uppercase italic">UZS</span>
              <button
                onClick={() => setAmountText("125 400")}
                className="h-10 px-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-[10px] font-black text-[#1e9f6b] uppercase tracking-widest hover:bg-[#1e9f6b] hover:text-white hover:border-[#1e9f6b] transition-all shadow-sm active:scale-95"
              >
                {t("common.max")}
              </button>
            </div>
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={submit}
          disabled={isPaying || !selectedCardId || amount < 1000}
          className="w-full h-16 bg-[#1e9f6b] hover:bg-[#0d7a52] disabled:opacity-50 disabled:grayscale text-white rounded-[24px] font-black text-sm uppercase tracking-[0.2em] shadow-2xl shadow-[#1e9f6b]/30 transition-all flex items-center justify-center gap-3 active:scale-[0.98] mt-4"
        >
          {isPaying ? (
            <div className="flex items-center gap-3">
              <span className="h-5 w-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
              <span>{t("common.processing")}</span>
            </div>
          ) : (
            <>
              {t("billing.pay.confirm")}
              <span className="material-symbols-outlined text-[20px]">arrow_forward</span>
            </>
          )}
        </button>
      </div>
    </BaseModal>
  );
}

